import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Archive,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  Filter,
  RefreshCw,
  Search,
  Settings,
  Tag,
  X,
} from 'lucide-react';
import { useMount } from 'react-use';
import { MinimalCard, CleanButton } from 'components/styled';
import { SearchInput } from 'components/SearchInput';
import DatePicker from 'components/DatePicker';
import useUrl from 'hooks/useUrl';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import { useTenantDropdown } from 'TenantModule/hooks/useTenantDropdown';
import { useTenants } from 'TenantModule/hooks/useTenants';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import useSandboxCreatorSelector from 'SandboxModule/hooks/useSandboxCreatorSelector';
import useSandboxStatusSelector from 'SandboxModule/hooks/useSandboxStatusSelector';
import { RealmTypes } from 'RealmModule/constants/realm';
import { FetchSandboxParams } from 'SandboxModule/types';
import { useEventSubscription } from 'core/event/useEventSubscription';
import { AppEvents } from 'core/event/constants';

type RealmsSearchFiltersProps = {
  realmType: RealmTypes;
  /**
   * Fired on every submit (Search, Reset, URL-driven search change) with the
   * submitted search text. GET /realm/all combines its two text params
   * (realmIdSearchKey, teamNameSearchKey) with AND, so a single search bar
   * matching realm ID OR team name is applied client-side over the
   * server-filtered list instead.
   */
  onSubmit: (payload: { searchText: string }) => void;
};

// DatePicker fires onChange while the user types; incomplete input comes
// through as "NaN-NaN-NaN", which would break the backend date comparison
const isValidDateString = (value?: string | null): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);

/**
 * Search bar + expandable advanced filters for the realm list pages
 * (Sandboxes, Subscriptions), following the TeamsV2Page/MembersPage pattern:
 * draft filter state applies only on explicit submit (Enter or Search button),
 * and the structured filters are sent server-side to GET /realm/all.
 */
const RealmsSearchFilters = ({ realmType, onSubmit }: RealmsSearchFiltersProps) => {
  const theme = useTheme();
  const { queryParams, pushQueryParams } = useUrl();
  const { fetchSandboxes, getSandboxesByIsoState } = useSandboxes();
  const { isRequesting } = getSandboxesByIsoState(SandboxIsoStateIds.SandboxTable);

  const [searchInput, setSearchInput] = useState<string>(queryParams.get('q') || '');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [tenant, setTenant] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [includeArchived, setIncludeArchived] = useState(true);
  const [createdAtStartDate, setCreatedAtStartDate] = useState<string | null>(null);
  const [createdAtEndDate, setCreatedAtEndDate] = useState<string | null>(null);
  // Last submitted params, used to refetch with the same filters (e.g. after
  // a realm is created) without picking up unsubmitted draft changes.
  // A ref keeps the CreateRealmSuccess callback identity stable.
  const submittedParamsRef = useRef<Partial<FetchSandboxParams> | null>(null);

  const { selectedStatuses, SandboxStatusSelector, handleClearSelectedStatuses } =
    useSandboxStatusSelector({ variant: 'outlined' });
  const {
    selectedCreators,
    SandboxCreatorSelector,
    handleClearSelectedCreators,
    fetchSandboxCreators,
  } = useSandboxCreatorSelector({ variant: 'outlined' });

  useMount(() => {
    fetchSandboxCreators();
  });

  // Keep the applied search in sync with the URL `q` param (initial load,
  // back/forward navigation, in-app links). Guarded by a ref because
  // queryParams is rebuilt on every render.
  const prevUrlSearchRef = useRef<string | null>(null);
  useEffect(() => {
    const urlSearchText = queryParams.get('q') || '';
    if (prevUrlSearchRef.current === urlSearchText) return;
    const isFirstRun = prevUrlSearchRef.current === null;
    prevUrlSearchRef.current = urlSearchText;
    if (isFirstRun && !urlSearchText) return;
    setSearchInput(urlSearchText);
    onSubmit({ searchText: urlSearchText });
  }, [queryParams, onSubmit]);

  const buildFetchParams = useCallback((): Partial<FetchSandboxParams> => {
    return {
      realmTypes: [realmType],
      // The backend coerces any present includeArchived value to true (the
      // string "false" is truthy), so the param must be omitted to exclude
      // archived realms
      includeArchived: includeArchived ? true : undefined,
      ...(tenant ? { tenant } : {}),
      ...(createdAtStartDate ? { createdAtStartDate } : {}),
      ...(createdAtEndDate ? { createdAtEndDate } : {}),
      ...(selectedStatuses.length ? { statuses: selectedStatuses } : {}),
      ...(selectedCreators.length ? { creators: selectedCreators.map(String) } : {}),
      ...(tags.length ? { tags } : {}),
    };
  }, [
    realmType,
    includeArchived,
    tenant,
    createdAtStartDate,
    createdAtEndDate,
    selectedStatuses,
    selectedCreators,
    tags,
  ]);

  const onSearch = () => {
    // Matches the disabled Search button; also guards Enter-key submits so
    // overlapping /realm/all responses can't land out of order
    if (isRequesting) return;
    const searchText = searchInput.trim();
    setSearchInput(searchText);
    prevUrlSearchRef.current = searchText;
    const mergedParams = Object.fromEntries(queryParams.entries());
    if (searchText) mergedParams.q = searchText;
    else delete mergedParams.q;
    pushQueryParams(mergedParams);
    onSubmit({ searchText });
    const params = buildFetchParams();
    submittedParamsRef.current = params;
    fetchSandboxes(SandboxIsoStateIds.SandboxTable, params);
  };

  const onReset = () => {
    // Same in-flight guard as onSearch — overlapping /realm/all responses
    // could otherwise land out of order and show stale results
    if (isRequesting) return;
    setTenant('');
    setTags([]);
    setTagInput('');
    setIncludeArchived(true);
    setCreatedAtStartDate(null);
    setCreatedAtEndDate(null);
    handleClearSelectedStatuses();
    handleClearSelectedCreators();
    setSearchInput('');
    prevUrlSearchRef.current = '';
    const mergedParams = Object.fromEntries(queryParams.entries());
    delete mergedParams.q;
    pushQueryParams(mergedParams);
    onSubmit({ searchText: '' });
    submittedParamsRef.current = null;
    fetchSandboxes(SandboxIsoStateIds.SandboxTable, {
      realmTypes: [realmType],
    });
  };

  // Keep the table fresh after a realm is created, preserving the last
  // submitted filters
  useEventSubscription({
    callback: useCallback(() => {
      fetchSandboxes(
        SandboxIsoStateIds.SandboxTable,
        submittedParamsRef.current || { realmTypes: [realmType] },
      );
    }, [fetchSandboxes, realmType]),
    eventName: AppEvents.CreateRealmSuccess,
  });

  const handleAddTag = () => {
    // The backend interpolates tags into a SQL array literal, so quotes
    // would break the query
    const value = tagInput.trim().replace(/'/g, '');
    if (!value) return;
    setTags((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setTagInput('');
  };

  const removeTag = (value: string) => setTags((prev) => prev.filter((tag) => tag !== value));

  // Tenant selector (page-local filter, does not switch global tenant)
  const { openTenantDropdown } = useTenantDropdown();
  const { tenantsHash, tenantOrder } = useTenants({ isOff: false });
  const selectedTenantIdForFilter = useMemo(() => {
    if (!tenant) return null;
    return tenantOrder.find((id) => tenantsHash[id]?.tenantName === tenant) || null;
  }, [tenant, tenantOrder, tenantsHash]);

  const onTenantSelectorClick: React.MouseEventHandler<HTMLElement> = (e) => {
    openTenantDropdown({
      target: e.currentTarget,
      shouldSkipSetTenant: true,
      onSelect: ({ tenant: selectedTenant }) => setTenant(selectedTenant.tenantName),
    });
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (tenant) count++;
    if (selectedStatuses.length) count++;
    if (selectedCreators.length) count++;
    if (tags.length) count++;
    if (createdAtStartDate || createdAtEndDate) count++;
    if (!includeArchived) count++;
    return count;
  }, [
    tenant,
    selectedStatuses,
    selectedCreators,
    tags,
    createdAtStartDate,
    createdAtEndDate,
    includeArchived,
  ]);

  return (
    <>
      {/* Search and Quick Actions */}
      <MinimalCard $hoverEffect={false} mb={3}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <SearchInput
            fullWidth
            placeholder="Search by realm ID or team name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
            InputProps={{
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearchInput('')}
                  >
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 300 }}
          />

          <CleanButton
            variant="contained"
            startIcon={<Search size={18} />}
            onClick={onSearch}
            disabled={isRequesting}
          >
            Search
          </CleanButton>

          <CleanButton
            variant="outlined"
            startIcon={filtersExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            endIcon={
              activeFilterCount > 0 ? (
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </Box>
              ) : undefined
            }
            onClick={() => setFiltersExpanded((prev) => !prev)}
          >
            Filters
          </CleanButton>

          <CleanButton variant="outlined" startIcon={<RefreshCw size={18} />} onClick={onReset}>
            Reset
          </CleanButton>
        </Box>
      </MinimalCard>

      {/* Expandable Filters */}
      <Collapse in={filtersExpanded}>
        <MinimalCard $hoverEffect={false} mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Filter size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" fontWeight={600}>
                Advanced Filters
              </Typography>
            </Box>
            {activeFilterCount > 0 && (
              <CleanButton
                size="small"
                variant="text"
                startIcon={<X size={16} />}
                onClick={onReset}
              >
                Clear All
              </CleanButton>
            )}
          </Box>

          {/* Organization */}
          <FilterSection mb={3}>
            <FilterSectionTitle icon={<Building2 size={16} />} title="Organization" />
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <TenantSelector
                onClick={onTenantSelectorClick}
                tenantId={selectedTenantIdForFilter}
                onClear={() => setTenant('')}
              />
            </Box>
          </FilterSection>

          {/* Realm Attributes */}
          <FilterSection mb={3}>
            <FilterSectionTitle icon={<Settings size={16} />} title="Realm Attributes" />
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              {SandboxStatusSelector}
              {SandboxCreatorSelector}
            </Box>
          </FilterSection>

          {/* Tags */}
          <FilterSection mb={3}>
            <FilterSectionTitle icon={<Tag size={16} />} title="Tags" />
            <TextField
              fullWidth
              size="small"
              label="Tag"
              placeholder="Enter tag and press Enter"
              helperText="Matches realms containing all of the tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddTag();
              }}
            />
            {tags.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                {tags.map((tag) => (
                  <Chip key={tag} color="primary" label={tag} onDelete={() => removeTag(tag)} />
                ))}
              </Box>
            )}
          </FilterSection>

          {/* Created Date */}
          <FilterSection mb={3}>
            <FilterSectionTitle icon={<Calendar size={16} />} title="Created Date" />
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <DatePicker
                label="Created after"
                value={createdAtStartDate}
                onChange={(newDate) =>
                  setCreatedAtStartDate(isValidDateString(newDate) ? newDate : null)
                }
              />
              <DatePicker
                label="Created before"
                value={createdAtEndDate}
                onChange={(newDate) =>
                  setCreatedAtEndDate(isValidDateString(newDate) ? newDate : null)
                }
              />
            </Box>
            {(createdAtStartDate || createdAtEndDate) && (
              <Box mt={1}>
                <CleanButton
                  size="small"
                  variant="text"
                  startIcon={<X size={16} />}
                  onClick={() => {
                    setCreatedAtStartDate(null);
                    setCreatedAtEndDate(null);
                  }}
                >
                  Clear dates
                </CleanButton>
              </Box>
            )}
          </FilterSection>

          {/* Options */}
          <FilterSection>
            <FilterSectionTitle icon={<Archive size={16} />} title="Options" />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                  size="small"
                />
              }
              label="Include archived"
            />
          </FilterSection>
        </MinimalCard>
      </Collapse>
    </>
  );
};

const FilterSection = ({ children, mb }: { children: React.ReactNode; mb?: number }) => (
  <Box
    sx={{
      padding: 3,
      borderRadius: 2,
      // Matches TeamsV2Page's FilterSection border colors
      border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#1f1f1f' : '#e5e5e5'}`,
      mb,
    }}
  >
    {children}
  </Box>
);

const FilterSectionTitle = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
  <Typography
    variant="subtitle2"
    sx={{
      mb: 2,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}
  >
    {icon} {title}
  </Typography>
);

export default RealmsSearchFilters;
